import { motion } from "framer-motion";
import { type FormEvent, useState } from "react";

import { API_BASE } from "../../config";
import { content } from "../../content/content";
import { fadeUp } from "../../hooks/useScrollReveal";

type Status = "idle" | "sending" | "sent" | "error";

export function Contact() {
  const [name, setName] = useState("");
  const [contact, setContact] = useState("");
  const [message, setMessage] = useState("");
  const [status, setStatus] = useState<Status>("idle");

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    const trimmedName = name.trim();
    const trimmedContact = contact.trim();
    if (!trimmedName || !trimmedContact || status === "sending") {
      return;
    }
    setStatus("sending");
    try {
      const response = await fetch(`${API_BASE}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: trimmedName,
          contact: trimmedContact,
          message: message.trim() || undefined,
        }),
      });
      if (!response.ok) {
        setStatus("error");
        return;
      }
      setStatus("sent");
    } catch {
      setStatus("error");
    }
  }

  const isSending = status === "sending";

  return (
    <section
      id="contact"
      className="relative overflow-hidden bg-ink py-28 md:py-40"
    >
      <div
        className="absolute top-0 right-0 left-0 selvedge-dark"
        aria-hidden="true"
      />

      <div className="relative mx-auto max-w-[1400px] px-6 md:px-10">
        <motion.div
          className="mx-auto max-w-2xl text-center"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <p
            data-edit-id="content.contact.eyebrow"
            className="mb-4 font-body text-sm font-medium tracking-widest text-saffron uppercase"
          >
            {content.contact.eyebrow}
          </p>
          <h2
            data-edit-id="content.contact.headline"
            className="text-balance font-display text-[clamp(2rem,5vw,3.8rem)] leading-[1.15] text-white"
          >
            {content.contact.headline}
          </h2>
          <p
            data-edit-id="content.contact.body"
            className="mx-auto mt-6 max-w-lg font-body text-lg leading-relaxed text-white/60"
          >
            {content.contact.body}
          </p>
        </motion.div>

        <motion.div
          className="mx-auto mt-12 max-w-lg"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {status === "sent" ? (
            <div className="rounded-2xl border border-saffron/30 bg-warm-white/[0.04] p-10 text-center">
              <div className="font-display text-2xl text-saffron">✦</div>
              <p className="mt-4 font-display text-2xl text-white">
                Thank you, {name.split(/\s+/)[0] ?? name}.
              </p>
              <p className="mt-3 font-body text-sm leading-relaxed text-white/65">
                We've received your message. Someone from the Sadafal Education
                team will write back to you soon.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-3">
              <div className="grid gap-3 sm:grid-cols-2">
                <input
                  type="text"
                  placeholder="Your name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  disabled={isSending}
                  aria-label="Your name"
                  className="w-full rounded-xl border border-warm-white/15 bg-warm-white/[0.04] px-5 py-3.5 font-body text-sm text-white placeholder-white/40 outline-none transition-colors focus:border-saffron focus:bg-warm-white/[0.06] disabled:opacity-60"
                />
                <input
                  type="email"
                  placeholder="Email"
                  value={contact}
                  onChange={(e) => setContact(e.target.value)}
                  required
                  disabled={isSending}
                  aria-label="Email"
                  className="w-full rounded-xl border border-warm-white/15 bg-warm-white/[0.04] px-5 py-3.5 font-body text-sm text-white placeholder-white/40 outline-none transition-colors focus:border-saffron focus:bg-warm-white/[0.06] disabled:opacity-60"
                />
              </div>
              <textarea
                placeholder="Anything you'd like us to know? (optional)"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={4}
                disabled={isSending}
                aria-label="Message (optional)"
                className="w-full resize-none rounded-xl border border-warm-white/15 bg-warm-white/[0.04] px-5 py-3.5 font-body text-sm text-white placeholder-white/40 outline-none transition-colors focus:border-saffron focus:bg-warm-white/[0.06] disabled:opacity-60"
              />
              <div className="flex flex-wrap items-center gap-3 pt-1">
                <button
                  type="submit"
                  disabled={isSending}
                  className="rounded-2xl bg-saffron px-8 py-3.5 font-body text-sm font-medium text-warm-white transition-colors duration-300 hover:bg-saffron-dark disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:bg-saffron"
                >
                  {isSending ? "Sending…" : content.contact.primaryButton}
                </button>
                <a
                  data-edit-id="content.contact.secondaryButton"
                  href={content.contact.secondaryHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="rounded-2xl border border-warm-white/20 px-8 py-3.5 font-body text-sm font-medium text-warm-white/85 transition-colors duration-300 hover:border-warm-white/40 hover:text-warm-white"
                >
                  {content.contact.secondaryButton}
                </a>
              </div>
              {status === "error" ? (
                <p
                  role="alert"
                  className="pt-2 font-body text-sm italic text-white/55"
                >
                  Couldn't send your message — please try again in a moment.
                </p>
              ) : null}
            </form>
          )}
        </motion.div>

        <motion.div
          className="mx-auto mt-24 max-w-xl text-center"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <div className="rounded-2xl border border-warm-white/15 bg-warm-white/[0.03] p-8">
            <p
              data-edit-id="content.contact.docEyebrow"
              className="font-body text-xs font-medium tracking-widest text-violet-light uppercase"
            >
              {content.contact.docEyebrow}
            </p>
            <p
              data-edit-id="content.contact.docQuote"
              className="mt-3 font-display text-lg text-white/80 italic"
            >
              &ldquo;{content.contact.docQuote}&rdquo;
            </p>
            <a
              data-edit-id="content.contact.docLinkText"
              href={content.contact.docHref}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-4 inline-flex items-center gap-2 font-body text-sm text-saffron transition-opacity hover:opacity-70"
            >
              <svg viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4">
                <path d="M8 5v14l11-7z" />
              </svg>
              {content.contact.docLinkText}
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
